import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { logger } from '@zep/core-logger';

import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useUser } from '@/lib/auth';
import { useShowSaveToFolderModal } from '@/shared/features/quiz-folder';

interface UseDuplicateLockedHandlerParams {
  quizId: string;
  quizData: QuizSetDetailsRes;
}

/**
 * useDuplicateLockedHandler - 잠긴 퀴즈 복사 핸들러
 *
 * @description
 * 잠긴 퀴즈 복사 버튼 클릭 시 동작을 처리합니다.
 * - 비로그인 사용자: 로그인 페이지로 리다이렉트 (콜백 URL 포함)
 * - 로그인 사용자: 저장 위치를 고르면 잠긴 상태의 내 퀴즈로 저장
 */
export const useDuplicateLockedHandler = ({
  quizId,
  quizData,
}: UseDuplicateLockedHandlerParams) => {
  const router = useRouter();
  const { user } = useUser();
  const showSaveToFolderModal = useShowSaveToFolderModal();

  return useCallback(() => {
    logger.track('enter_duplicate_locked', {
      location: 'detail',
      spaceId: quizData.id,
      title: quizData.title,
    });

    // 비로그인 상태면 로그인 페이지로 리다이렉트 (로그인 후 현재 페이지로 돌아옴)
    if (!user) {
      const currentUrl = `/quiz/${quizId}`;
      void router.push({
        pathname: '/login',
        query: { callbackUrl: currentUrl },
      });
      return;
    }

    /*
      잠긴 퀴즈도 그대로 저장한다 — 서버가 내용을 유지한 채 **내 플랜 기준으로 잠긴**
      내 퀴즈를 만든다(응답의 `isLocked`·`requiredPlanType`). 저장 전에 결제를 요구하지 않는다.
    */
    void showSaveToFolderModal({
      id: quizData.id,
      intent: 'copy',
    });
  }, [router, user, quizId, quizData, showSaveToFolderModal]);
};
